import * as state from './state.js';
import { escapeHtml, showToast } from './dom.js';
import { sendYAMLToPreview } from './preview.js';
import { getDoc, getSelectionText, setDoc } from './editor-view.js';

export function getCurrentEditorYaml() {
    return getDoc(state.editor).trim();
}

export function resolveYamlContext() {
    if (state.selectedYamlContext) {
        return state.selectedYamlContext;
    }

    return getCurrentEditorYaml() || null;
}

export function toggleAISidebar() {
    const sidebar = document.getElementById('ai-sidebar');
    const container = document.querySelector('.playground-container');
    const toggleBtn = document.getElementById('ai-sidebar-toggle');

    state.setAiSidebarOpen(!state.aiSidebarOpen);

    if (state.aiSidebarOpen) {
        sidebar.classList.add('open');
        container.classList.add('with-sidebar');
        toggleBtn.classList.add('hidden');
        document.getElementById('ai-chat-input').focus();
    } else {
        sidebar.classList.remove('open');
        container.classList.remove('with-sidebar');
        toggleBtn.classList.remove('hidden');
    }
}

export function extractSourceFromYaml(yaml) {
    if (!yaml) return null;
    // Top-level source: value (strip surrounding quotes)
    const topLevel = yaml.match(/^source:\s*['"]?([A-Za-z0-9_.-]+)['"]?/m);
    if (topLevel) return topLevel[1];
    // sources.default: — scan line-by-line to stay inside the sources: block
    const lines = yaml.split('\n');
    let inSources = false;
    for (const line of lines) {
        if (!inSources) {
            if (/^sources:\s*$/.test(line.trimEnd())) inSources = true;
        } else {
            if (line.trim().length > 0 && /^[^ \t]/.test(line)) break;
            const m = line.match(/^[ \t]{1,4}default:\s*['"]?([A-Za-z0-9_.-]+)['"]?/);
            if (m) return m[1];
        }
    }
    return null;
}

export function insertYamlContext() {
    const selectedText = getSelectionText(state.editor);
    if (!selectedText) {
        showToast('Please select some YAML code first');
        return;
    }

    state.setSelectedYamlContext(selectedText);
    showToast('Context inserted! You can now ask questions about this code.');

    addAIMessage('user', `I've selected this YAML code:\n\n\`\`\`yaml\n${selectedText}\n\`\`\``);
}

export function addAIMessage(role, content, modifier = null) {
    const messagesContainer = document.getElementById('ai-chat-messages');
    const messageDiv = document.createElement('div');
    messageDiv.className = modifier ? `ai-message ${role} ${modifier}` : `ai-message ${role}`;

    let formattedContent;

    if (typeof window.marked !== 'undefined') {
        try {
            window.marked.setOptions({
                breaks: true,
                gfm: true,
                headerIds: false,
                mangle: false
            });
            formattedContent = window.marked.parse(content);
        } catch (e) {
            console.warn('Markdown parsing failed, using fallback:', e);
            formattedContent = formatMarkdownFallback(content);
        }
    } else {
        formattedContent = formatMarkdownFallback(content);
    }

    messageDiv.innerHTML = formattedContent;
    messagesContainer.appendChild(messageDiv);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
}

export function formatMarkdownFallback(content) {
    let formatted = escapeHtml(content);

    formatted = formatted.replace(/```(\w+)?\n([\s\S]*?)```/g,
        (_, lang, code) => `<pre><code class="language-${lang || 'text'}">${code.trim()}</code></pre>`);

    formatted = formatted.replace(/`([^`]+)`/g, '<code>$1</code>');
    formatted = formatted.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    formatted = formatted.replace(/^## (.*$)/gim,  '<h2>$1</h2>');
    formatted = formatted.replace(/^# (.*$)/gim,   '<h1>$1</h1>');
    formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/\*(.+?)\*/g,    '<em>$1</em>');
    formatted = formatted.replace(/^[\*\-] (.+)$/gim, '<li>$1</li>');
    formatted = formatted.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
    formatted = formatted.replace(/\n/g, '<br>');

    return formatted;
}

export function createReasoningStatusElement() {
    const statusDiv = document.createElement('div');
    statusDiv.className = 'ai-thinking-status';
    statusDiv.innerHTML = `
        <span class="ai-thinking-indicator">●</span>
        <span class="ai-thinking-text"></span>
    `;
    return statusDiv;
}

export function updateReasoningStatus(element, status) {
    // element can be null: SSE status events may arrive after the indicator was
    // removed (content already streaming). Callers recreate it when they want it
    // visible; guard here so a late/stray status never throws.
    if (!element) return;
    const textElement = element.querySelector('.ai-thinking-text');
    if (textElement) {
        textElement.textContent = status;
    }
    const messagesContainer = document.getElementById('ai-chat-messages');
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
}

export function insertGeneratedYAML(yamlContent, { showToastMessage = true } = {}) {
    setDoc(state.editor, yamlContent);
    if (showToastMessage) {
        showToast('YAML inserted into editor');
    }
    sendYAMLToPreview();
}

function handleAIStreamAuxiliaryEvent(data, reasoningStatusElement) {
    const messagesContainer = document.getElementById('ai-chat-messages');

    if (data.type === 'editor_update' && data.yaml) {
        insertGeneratedYAML(data.yaml, { showToastMessage: false });
        if (reasoningStatusElement) {
            updateReasoningStatus(reasoningStatusElement, 'Rendering dashboard...');
        }
        return true;
    }

    if (data.type === 'render' && data.result) {
        if (reasoningStatusElement) {
            updateReasoningStatus(reasoningStatusElement, 'Rendering dashboard...');
        }
        return true;
    }

    if (data.type === 'research_query') {
        const researchElement = document.createElement('div');
        researchElement.className = 'ai-research-query';
        researchElement.innerHTML = `
            <div class="ai-research-header">
                <span class="ai-research-icon">🔍</span>
                <span class="ai-research-title">Researching database...</span>
            </div>
            ${data.query_sql ? `
            <div class="ai-research-query-sql">
                <div class="ai-research-label">Query:</div>
                <pre><code>${escapeHtml(data.query_sql)}</code></pre>
            </div>
            ` : ''}
        `;
        messagesContainer.appendChild(researchElement);
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
        return true;
    }

    if (data.type === 'research_result' && data.result) {
        let researchElement = messagesContainer.querySelector('.ai-research-query:last-child');
        if (!researchElement) {
            researchElement = document.createElement('div');
            researchElement.className = 'ai-research-query';
            messagesContainer.appendChild(researchElement);
        }

        const result = data.result;
        if (result.success) {
            const rowCount = result.row_count || 0;
            const truncated = result.truncated ? ' (result truncated)' : '';
            const dataRows = result.data || [];

            researchElement.innerHTML = `
                <div class="ai-research-header">
                    <span class="ai-research-icon">✅</span>
                    <span class="ai-research-title">Research complete</span>
                </div>
                ${data.query_sql ? `
                <div class="ai-research-query-sql">
                    <div class="ai-research-label">Query:</div>
                    <pre><code>${escapeHtml(data.query_sql)}</code></pre>
                </div>
                ` : ''}
                <div class="ai-research-result">
                    <div class="ai-research-summary">
                        Found <strong>${rowCount}</strong> row${rowCount !== 1 ? 's' : ''}${truncated}
                        ${result.columns && result.columns.length > 0 ? ` with columns: <code>${result.columns.join('</code>, <code>')}</code>` : ''}
                    </div>
                    ${dataRows.length > 0 ? `
                    <div class="ai-research-data">
                        <div class="ai-research-label">Sample data:</div>
                        <div class="ai-research-table">
                            <table>
                                <thead>
                                    <tr>
                                        ${result.columns.map(col => `<th>${escapeHtml(col)}</th>`).join('')}
                                    </tr>
                                </thead>
                                <tbody>
                                    ${dataRows.slice(0, 10).map(row => `
                                        <tr>
                                            ${result.columns.map(col => {
                                                const val = row[col];
                                                const displayVal = val === null || val === undefined ? '<em>null</em>' : escapeHtml(String(val));
                                                return `<td>${displayVal}</td>`;
                                            }).join('')}
                                        </tr>
                                    `).join('')}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    ` : ''}
                </div>
            `;
        } else {
            researchElement.innerHTML = `
                <div class="ai-research-header">
                    <span class="ai-research-icon">❌</span>
                    <span class="ai-research-title">Research failed</span>
                </div>
                ${data.query_sql ? `
                <div class="ai-research-query-sql">
                    <div class="ai-research-label">Query:</div>
                    <pre><code>${escapeHtml(data.query_sql)}</code></pre>
                </div>
                ` : ''}
                <div class="ai-research-error">
                    <strong>Error:</strong> ${escapeHtml(result.error || 'Unknown error')}
                </div>
            `;
        }
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
        return true;
    }

    return false;
}

export async function sendAIMessage() {
    const chatInput = document.getElementById('ai-chat-input');
    const sendBtn = document.getElementById('ai-send-btn');
    const message = chatInput.value.trim();

    if (!message) return;

    addAIMessage('user', message);
    state.conversationHistory.push({ role: 'user', content: message });

    chatInput.value = '';
    sendBtn.disabled = true;
    sendBtn.textContent = 'Sending...';

    let reasoningStatusElement = createReasoningStatusElement();
    const messagesContainer = document.getElementById('ai-chat-messages');
    messagesContainer.appendChild(reasoningStatusElement);
    updateReasoningStatus(reasoningStatusElement, 'Connecting to AI...');

    let promptType = 'yaml_generation';
    const lowerMessage = message.toLowerCase();
    const yamlContextForRequest = resolveYamlContext();
    if (lowerMessage.includes('report') || lowerMessage.includes('report about')) {
        promptType = 'report_generation';
    } else if (lowerMessage.includes('dashboard') || lowerMessage.includes('dashboard about')) {
        promptType = 'dashboard_design';
    } else if (lowerMessage.includes('database') || lowerMessage.includes('explore') || lowerMessage.includes('what\'s in')) {
        promptType = 'database_exploration';
    }

    let assistantMessageElement = null;
    let assistantContent = '';

    try {
        console.log('Starting streaming request...');
        const currentYaml = getCurrentEditorYaml();
        const response = await fetch(state.withProject('/api/ai/chat'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                message: message,
                prompt_type: promptType,
                source_name: extractSourceFromYaml(currentYaml),
                yaml_context: yamlContextForRequest,
                conversation_history: state.conversationHistory.slice(-10),
                stream: true,
            }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        console.log('Streaming response received, starting to read...');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                console.log('Stream finished');
                break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                if (!line.trim()) continue;

                if (line.startsWith('data: ')) {
                    try {
                        const data = JSON.parse(line.slice(6));
                        console.log('Received streaming data:', data);

                        if ((data.type === 'reasoning' || data.type === 'thinking') && data.status) {
                            // A status can arrive after content already streamed (which
                            // removes the indicator) — e.g. the agent runs another tool
                            // mid-response. Recreate the indicator so it stays visible
                            // rather than dereferencing a removed (null) element.
                            if (!reasoningStatusElement) {
                                reasoningStatusElement = createReasoningStatusElement();
                                messagesContainer.appendChild(reasoningStatusElement);
                            }
                            updateReasoningStatus(reasoningStatusElement, data.status);
                        } else if (handleAIStreamAuxiliaryEvent(data, reasoningStatusElement)) {
                            // Auxiliary events update the editor or status UI without chat text.
                        } else if (data.type === 'content' && data.delta) {
                            if (!assistantMessageElement) {
                                assistantMessageElement = document.createElement('div');
                                assistantMessageElement.className = 'ai-message assistant';
                                assistantMessageElement.innerHTML = '<div class="ai-message-content"></div>';
                                document.getElementById('ai-chat-messages').appendChild(assistantMessageElement);
                                if (reasoningStatusElement) {
                                    reasoningStatusElement.remove();
                                    reasoningStatusElement = null;
                                }
                            }
                            assistantContent += data.delta;
                            const contentDiv = assistantMessageElement.querySelector('.ai-message-content');
                            if (typeof window.marked !== 'undefined') {
                                contentDiv.innerHTML = window.marked.parse(assistantContent);
                            } else {
                                contentDiv.textContent = assistantContent;
                            }
                            document.getElementById('ai-chat-messages').scrollTop =
                                document.getElementById('ai-chat-messages').scrollHeight;
                        } else if (data.type === 'done') {
                            if (reasoningStatusElement) {
                                reasoningStatusElement.remove();
                                reasoningStatusElement = null;
                            }

                            if (data.reasoning_summary) {
                                const reasoningMsg = document.createElement('div');
                                reasoningMsg.className = 'ai-thinking-status';
                                reasoningMsg.innerHTML = `
                                    <span class="ai-thinking-indicator">💭</span>
                                    <span class="ai-thinking-text">Reasoning: ${escapeHtml(data.reasoning_summary)}</span>
                                `;
                                document.getElementById('ai-chat-messages').appendChild(reasoningMsg);
                                setTimeout(() => {
                                    if (reasoningMsg.parentNode) {
                                        reasoningMsg.remove();
                                    }
                                }, 5000);
                            }

                            if (!assistantMessageElement && data.response) {
                                assistantMessageElement = addAIMessage('assistant', data.response);
                            } else if (assistantMessageElement && data.response) {
                                const contentDiv = assistantMessageElement.querySelector('.ai-message-content');
                                if (typeof window.marked !== 'undefined') {
                                    contentDiv.innerHTML = window.marked.parse(data.response);
                                } else {
                                    contentDiv.textContent = data.response;
                                }
                            }
                            assistantContent = data.response || assistantContent;
                            state.conversationHistory.push({ role: 'assistant', content: assistantContent });

                            if (data.yaml) {
                                const yamlMessage = document.createElement('div');
                                yamlMessage.className = 'ai-message assistant';
                                const yamlContent = data.yaml;
                                yamlMessage.innerHTML = `
                                    <div style="margin-bottom: 8px;">I've generated the following YAML and inserted it into the editor:</div>
                                    <pre><code>${escapeHtml(yamlContent)}</code></pre>
                                `;
                                document.getElementById('ai-chat-messages').appendChild(yamlMessage);
                                insertGeneratedYAML(yamlContent);
                            }
                        } else if (data.type === 'error') {
                            if (reasoningStatusElement) {
                                reasoningStatusElement.remove();
                                reasoningStatusElement = null;
                            }

                            if (data.yaml) {
                                insertGeneratedYAML(data.yaml);

                                let errorMessage = `Error: ${data.message}`;

                                if (data.errors && data.errors.length > 0) {
                                    errorMessage += '\n\n**Validation Errors:**\n';
                                    data.errors.forEach((err, idx) => {
                                        errorMessage += `${idx + 1}. ${escapeHtml(String(err))}\n`;
                                    });
                                }

                                if (data.tips && data.tips.length > 0) {
                                    errorMessage += '\n\n**Tips:**\n';
                                    data.tips.forEach((tip) => {
                                        errorMessage += `- ${escapeHtml(tip)}\n`;
                                    });
                                }

                                errorMessage += '\n\nThe YAML has been inserted into the editor above. Please review and fix the errors manually.';

                                addAIMessage('assistant', errorMessage, 'error');
                            } else {
                                addAIMessage('assistant', `Error: ${data.message}`, 'error');
                            }
                        }
                    } catch (e) {
                        console.error('Error parsing SSE data:', e);
                    }
                }
            }
        }
    } catch (error) {
        if (reasoningStatusElement) {
            reasoningStatusElement.remove();
        }
        addAIMessage('assistant', `Error: ${error.message}`, 'error');
    } finally {
        sendBtn.disabled = false;
        sendBtn.textContent = 'Send';
        chatInput.focus();
    }
}

export function chatAboutChart(chartId, chartTitle) {
    if (!state.aiSidebarOpen) {
        toggleAISidebar();
    }

    addAIMessage('user', `I want to chat about this chart:\n\n**${chartTitle}**\n(ID: ${chartId})`);

    setTimeout(() => {
        sendAIMessageWithContext(
            `Tell me about the chart "${chartTitle}" (ID: ${chartId}). What does it show?`,
            `Chart ID: ${chartId}\nChart Title: ${chartTitle}`
        );
    }, 100);
}

export async function sendAIMessageWithContext(message, chartContext) {
    const sendBtn = document.getElementById('ai-send-btn');
    const chatInput = document.getElementById('ai-chat-input');

    addAIMessage('user', message);
    state.conversationHistory.push({ role: 'user', content: message });

    sendBtn.disabled = true;
    sendBtn.textContent = 'Sending...';

    let assistantMessageElement = null;
    let assistantContent = '';
    let reasoningStatusElement = null;

    const yamlContextForRequest = resolveYamlContext();

    try {
        const currentYaml = getCurrentEditorYaml();
        const response = await fetch(state.withProject('/api/ai/chat'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                message: message,
                prompt_type: 'yaml_generation',
                source_name: extractSourceFromYaml(currentYaml),
                yaml_context: yamlContextForRequest,
                chart_context: chartContext,
                conversation_history: state.conversationHistory.slice(-10),
                stream: true,
            }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    try {
                        const data = JSON.parse(line.slice(6));

                        if (data.type === 'reasoning' && data.status) {
                            if (!reasoningStatusElement) {
                                reasoningStatusElement = createReasoningStatusElement();
                                document.getElementById('ai-chat-messages').appendChild(reasoningStatusElement);
                            }
                            updateReasoningStatus(reasoningStatusElement, data.status);
                        } else if (data.type === 'thinking' && data.status) {
                            if (!reasoningStatusElement) {
                                reasoningStatusElement = createReasoningStatusElement();
                                document.getElementById('ai-chat-messages').appendChild(reasoningStatusElement);
                            }
                            updateReasoningStatus(reasoningStatusElement, data.status);
                        } else if (handleAIStreamAuxiliaryEvent(data, reasoningStatusElement)) {
                            // Shared SSE editor/render/research events are handled above.
                        } else if (data.type === 'content' && data.delta) {
                            if (!assistantMessageElement) {
                                assistantMessageElement = document.createElement('div');
                                assistantMessageElement.className = 'ai-message assistant';
                                assistantMessageElement.innerHTML = '<div class="ai-message-content"></div>';
                                document.getElementById('ai-chat-messages').appendChild(assistantMessageElement);
                                if (reasoningStatusElement) {
                                    reasoningStatusElement.remove();
                                    reasoningStatusElement = null;
                                }
                            }
                            assistantContent += data.delta;
                            const contentDiv = assistantMessageElement.querySelector('.ai-message-content');
                            if (typeof window.marked !== 'undefined') {
                                contentDiv.innerHTML = window.marked.parse(assistantContent);
                            } else {
                                contentDiv.textContent = assistantContent;
                            }
                            document.getElementById('ai-chat-messages').scrollTop =
                                document.getElementById('ai-chat-messages').scrollHeight;
                        } else if (data.type === 'done') {
                            if (reasoningStatusElement) {
                                reasoningStatusElement.remove();
                                reasoningStatusElement = null;
                            }

                            if (data.reasoning_summary) {
                                const reasoningMsg = document.createElement('div');
                                reasoningMsg.className = 'ai-thinking-status';
                                reasoningMsg.innerHTML = `
                                    <span class="ai-thinking-indicator">💭</span>
                                    <span class="ai-thinking-text">Reasoning: ${escapeHtml(data.reasoning_summary)}</span>
                                `;
                                document.getElementById('ai-chat-messages').appendChild(reasoningMsg);
                                setTimeout(() => {
                                    if (reasoningMsg.parentNode) {
                                        reasoningMsg.remove();
                                    }
                                }, 5000);
                            }

                            if (!assistantMessageElement && data.response) {
                                assistantMessageElement = addAIMessage('assistant', data.response);
                            } else if (assistantMessageElement && data.response) {
                                const contentDiv = assistantMessageElement.querySelector('.ai-message-content');
                                if (typeof window.marked !== 'undefined') {
                                    contentDiv.innerHTML = window.marked.parse(data.response);
                                } else {
                                    contentDiv.textContent = data.response;
                                }
                            }
                            assistantContent = data.response || assistantContent;
                            state.conversationHistory.push({ role: 'assistant', content: assistantContent });

                            if (data.yaml) {
                                const yamlMessage = document.createElement('div');
                                yamlMessage.className = 'ai-message assistant';
                                const yamlContent = data.yaml;
                                yamlMessage.innerHTML = `
                                    <div style="margin-bottom: 8px;">I've generated the following YAML and inserted it into the editor:</div>
                                    <pre><code>${escapeHtml(yamlContent)}</code></pre>
                                `;
                                document.getElementById('ai-chat-messages').appendChild(yamlMessage);
                                insertGeneratedYAML(yamlContent);
                            }
                        } else if (data.type === 'error') {
                            if (reasoningStatusElement) {
                                reasoningStatusElement.remove();
                                reasoningStatusElement = null;
                            }

                            if (data.yaml) {
                                insertGeneratedYAML(data.yaml);

                                let errorMessage = `Error: ${data.message}`;

                                if (data.errors && data.errors.length > 0) {
                                    errorMessage += '\n\n**Validation Errors:**\n';
                                    data.errors.forEach((err, idx) => {
                                        errorMessage += `${idx + 1}. ${escapeHtml(String(err))}\n`;
                                    });
                                }

                                if (data.tips && data.tips.length > 0) {
                                    errorMessage += '\n\n**Tips:**\n';
                                    data.tips.forEach((tip) => {
                                        errorMessage += `- ${escapeHtml(tip)}\n`;
                                    });
                                }

                                errorMessage += '\n\nThe YAML has been inserted into the editor above. Please review and fix the errors manually.';

                                addAIMessage('assistant', errorMessage, 'error');
                            } else {
                                addAIMessage('assistant', `Error: ${data.message}`, 'error');
                            }
                        }
                    } catch (e) {
                        console.error('Error parsing SSE data:', e);
                    }
                }
            }
        }
    } catch (error) {
        if (reasoningStatusElement) {
            reasoningStatusElement.remove();
        }
        addAIMessage('assistant', `Error: ${error.message}`, 'error');
    } finally {
        sendBtn.disabled = false;
        sendBtn.textContent = 'Send';
        chatInput.focus();
    }
}

export function initAISidebar() {
    const sidebar = document.getElementById('ai-sidebar');
    const toggleBtn = document.getElementById('ai-sidebar-toggle');
    const closeBtn = document.getElementById('ai-sidebar-close');
    const sendBtn = document.getElementById('ai-send-btn');
    const chatInput = document.getElementById('ai-chat-input');
    const insertContextBtn = document.getElementById('ai-insert-context-btn');

    toggleBtn.addEventListener('click', () => {
        toggleAISidebar();
    });

    closeBtn.addEventListener('click', () => {
        toggleAISidebar();
    });

    sendBtn.addEventListener('click', () => {
        sendAIMessage();
    });

    chatInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendAIMessage();
        }
    });

    insertContextBtn.addEventListener('click', () => {
        insertYamlContext();
    });

    document.addEventListener('keydown', (e) => {
        // With nothing highlighted, Cmd/Ctrl-L stays the browser's address bar.
        if ((e.metaKey || e.ctrlKey) && e.key === 'l' && getSelectionText(state.editor)) {
            e.preventDefault();
            insertYamlContext();
        }
    });

    addAIMessage('assistant', 'Hello! I can help you create dashboards, generate reports, or explore your database. What would you like to do?');

}
